import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	ViewChild,
	ViewEncapsulation,
	HostListener,
	ChangeDetectorRef,
	ChangeDetectionStrategy,
	Output,
	EventEmitter,
	AfterViewInit
} from "@angular/core";
import { Subscription } from "rxjs";
import { IdService } from "../shared/services/id.service";
import { PopoverService } from "../shared/services/popover.service";
import { trigger, state, style, animate, transition } from "@angular/animations";

@Component({
	selector: "rbc-tooltip",
	template: `
		<div
			#self
			[id]="id"
			[ngClass]="{ open: isOpen, 'rbc-tooltip-relative': !absolute }"
			class="rbc-tooltip"
			(click)="toggle($event)"
			(mouseleave)="hoverable ? onMouseOut($event): null"
			(mouseenter)="hoverable ? onMouseOver($event) : null"
		>
			<slot #triggerSlot>
				<ng-content select="[trigger]"> </ng-content>
			</slot>

			<ng-container *ngIf="!triggerSlot.children.length">
				<ng-container
					*ngTemplateOutlet="triggerDefaultTmp"
				></ng-container>
			</ng-container>
			<div
				#content
				[@opacity]="{value: isOpen}"
				(@opacity.start)="animationDone($event)"
				[id]="'tooltipContent' + id"
				class="rbc-tooltip-content"
				role="tooltip"
				tabindex="-1"
				role="tooltip"
				[ngStyle]="{
					'top': topGen + 'px',
					'left': (overflowLeft || leftGen) + 'px'
				}"
				[ngClass]="classGen">
				<ng-container *ngTemplateOutlet="headings"></ng-container>
				<span *ngIf="text" class="rbc-tooltip-text" [innerHTML]="text"></span>
				<div #contentSlot>
					<ng-content></ng-content>
				</div>
			</div>
		</div>

		<ng-template #headings>
			<ng-container *ngIf="title">
				<ng-container *ngIf="headingTag == 'h2'">
					<h2 class="rbc-tooltip-title" [innerHTML]="title"></h2>
				</ng-container>
				<ng-container *ngIf="headingTag == 'h3'">
					<h3 class="rbc-tooltip-title" [innerHTML]="title"></h3>
				</ng-container>
				<ng-container *ngIf="headingTag == 'h4'">
					<h4 class="rbc-tooltip-title" [innerHTML]="title"></h4>
				</ng-container>
				<ng-container *ngIf="headingTag == 'h5'">
					<h5 class="rbc-tooltip-title" [innerHTML]="title"></h5>
				</ng-container>
				<ng-container *ngIf="headingTag == 'h6'">
					<h6 class="rbc-tooltip-title" [innerHTML]="title"></h6>
				</ng-container>
			</ng-container>
		</ng-template>

		<ng-template #triggerDefaultTmp>
			<button
				#button
				*ngIf= "!triggerSlotExists"
				[attr.aria-label]= "labelTrigger"
				[attr.aria-describedby]= "isOpen ? 'tooltipContent' + id : null"
				[attr.aria-expanded]= "isOpen ? true: false"
				[attr.aria-haspopup]= "true"
				class="rbc-tooltip-trigger-icon"
				(click)="onButtonClick.emit($event)">
				<rbc-icon
					[type]= "iconWeight"
					[icon]= "icon ? icon : 'question-circle'"
					tabindex="-1"
					[attr.aria-hidden]="true"
				>
				</rbc-icon>
			</button>
		</ng-template>
	`,
	styles: [],
	providers: [PopoverService],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger("opacity", [
			state("true", style({
				opacity: 1
			})),
			state("false", style({
				opacity: 0,
				visibility: "hidden"

			})),
			state("void", style({
				opacity: 0,
				visibility: "hidden"

			})),
			transition("* => false", [style({
				opacity: 0,
				visibility: "hidden" }), animate("250ms ease-in")]),
			transition("* => true", animate("250ms ease-out"))
		])
	]
})
export class TooltipComponent implements OnDestroy {
	get isOpen(): boolean {
		return this.popoverService.isOpen || this.active;
	}
	set isOpen(change: boolean) {
		this.popoverService.isOpen = change;
	}

	get contentSlotExists(): boolean {
		// make sure it's not a angular comment
		return this.contentSlot
			? !!this.contentSlot.nativeElement.children.length
			: false;
	}
	get triggerSlotExists(): boolean {
		// make sure it's not a angular comment
		return this.triggerSlot
			? !!(this.triggerSlot.nativeElement as HTMLElement).children.length
			: false;
	}

	/* tooltip wrapper classes */
	get classGen() {
		return [
			this.position
				? `rbc-tooltip-${this.position}`
				: "rbc-tooltip-bottom",
			this.size === "small" && !this.title && !this.contentSlotExists
				? "rbc-tooltip-small"
				: "",
			this.size === "large" || this.title || this.contentSlotExists
				? "rbc-tooltip-large"
				: "",
			this.overflowLeft ? "rbc-tooltip-overflow" : "",
			this.positionX === "left" ? "rbc-tooltip-flush-left" : "",
			this.positionX === "right" ? "rbc-tooltip-flush-right" : "",
			this.positionX === "center" ? "rbc-tooltip-center" : ""
		];
	}

	@Input() active = false;
	@Input() size: "small" | "large" | "medium" = "medium";
	@Input() clickable = true;
	@Input() hoverable = false;
	@Input() icon = "question-circle";
	@Input() iconWeight?: "solid" | "regular" | "light" = "regular";
	@Input() id: string;
	@Input() labelTrigger = "Toggle tooltip";
	@Input() text: string;
	@Input() position?: "top" | "right" | "bottom" | "left" = "bottom";
	@Input() positionX?: "left" | "right" | "center" = "center";
	@Input() title: string;
	@Input() transition?: "opacity" | "opacity-transform" = "opacity-transform";
	@Input() absolute = false;
	@Input() headingTag: "h2" | "h3" | "h4" | "h5" | "h6" = "h5";


	@Output() popoverToggle = new EventEmitter();
	@Output() onButtonClick = new EventEmitter();

	@ViewChild("self", { static: false }) self: ElementRef;
	@ViewChild("button", { static: false }) button: ElementRef;
	@ViewChild("content", { static: false }) content: ElementRef;
	@ViewChild("contentSlot", { static: false }) contentSlot: ElementRef;
	@ViewChild("triggerSlot", { static: false }) triggerSlot: ElementRef;

	clickListener = this.$_Popover_toggleClose.bind(this);
	$popoverToggleSub: Subscription;

	constructor(
		private idService: IdService,
		private popoverService: PopoverService,
		private cdr: ChangeDetectorRef,
		private eRef: ElementRef
	) {
		this.id = this.idService.generate();
	}

	onMouseOver(event: Event) {
		event.preventDefault();
		this.hoverToggle(true)
	}

	onMouseOut(event: Event) {
		event.preventDefault();
		this.hoverToggle(false)
	}

	get topGen(): number {
		return this.popoverService.topGen();
	}
	get leftGen(): number {
		return this.popoverService.leftGen();
	}
	get overflowLeft(): number {
		return this.popoverService.overflowLeft();
	}

	animationDone(e) {
		if (e.toState === true && this.content) {
			if (this.content && this.self) {
				this.popoverService.syncProps({
					clickable: this.clickable,
					active: this.active,
					hover: this.hoverable,
					position: this.position,
					positionX: this.positionX,
					offset: 10,
					triggerRef: (this.self.nativeElement as HTMLElement),
					popoverRef: this.content.nativeElement,
					absolute: this.absolute,
					pageOffset: window.pageYOffset
				});
				this.popoverService.enter(this.content.nativeElement);
			}
		}
	}

	ngOnDestroy(): void {
		if (this.idService) {
			this.idService.remove(this.id);
		}
		if (this.$popoverToggleSub) {
			this.$popoverToggleSub.unsubscribe();
		}

		// ensure tooltip is fully removed
		this.isOpen = false;
	}


	toggle(e) {
		const content = this.content.nativeElement;
		if (this.clickable && !(content === e.target || (content && content.contains(e.target)))) {
			this.isOpen = !this.isOpen;
			this.popoverToggle.emit(this.isOpen);

			// allow tooltip to be closed on document click / esc key press
			if (this.isOpen) {
				['click', 'touchend', 'keydown'].forEach(handler => document.addEventListener(handler, this.clickListener));
			} else {
				['click', 'touchend', 'keydown'].forEach(handler => document.removeEventListener(handler, this.clickListener));
			}
		}
	}

	hoverToggle(toggle: boolean) {
		if (this.hoverable) {
			setTimeout(() => {
				this.isOpen = toggle;
				this.cdr.markForCheck();
			}, 250);
		}
	}

	$_Popover_toggleClose(e) {
		const el = this.self.nativeElement;
		const content = this.content.nativeElement;
		// when tabbing outside of the content, return focus to the trigger
		// and close the popover
		if ((e.key && e.key.toLowerCase().indexOf('tab') != -1)) {
			setTimeout(() => {
				if (!content.contains(document.activeElement)) {
					this.$_Popover_close(e);
				}
			});
		}

		if (
			(e.key && e.key.toLowerCase().indexOf('esc') != -1) ||
			(!(el === e.target || el.contains(e.target)) &&
			(content && !(content === e.target || content.contains(e.target))))
		) {
			this.$_Popover_close(e);
		}
	}
	$_Popover_close(e) {
		this.isOpen = false;
		this.cdr.markForCheck();
		if (this.absolute && this.button.nativeElement) {
			this.button.nativeElement.focus();
		}
		['click', 'touchend', 'keydown'].forEach(handler => document.removeEventListener(handler, this.clickListener));
	}

}
